import {
    SubscriptionStatus
} from './subscription-status';
import * as moment from 'moment';
import { Moment } from 'moment';
import { SubscriptionType } from './subscription-type';
import { SubscriptionPlan } from './subscription-plan';
import { SubscriptionInterval } from './subscription-interval';
import { SubscriptionTier } from './subscription-tier';
import { SubscriptionModel } from './subscription-model';
import { isCustomPlan } from '@core/utils/billing';
import { getLocalMomentFromUtc } from '@core/utils/date-time';
import { MOMENT_UNIT } from '@core/utils/moment';

/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */

export class BillingSubscription {
    public plan: string;
    public plan_id: SubscriptionPlan;
    public status: SubscriptionStatus;
    public interval: SubscriptionInterval;
    public trial_end: string;
    public cancellation_end: string;
    public days_to_billing: number;
    public period_start: string;
    public period_end: string;
    public metadata: {
        type: SubscriptionType;
        tier: SubscriptionTier;
        plan_id: SubscriptionPlan;
        model?: SubscriptionModel;
    };
    public SECONDS_PER_DAY = 86400;
    public price: number;
    public amount?: number;
    public quantity: number;
    public scheduled_plan: BillingSubscription;

    /**
     * Returns interval from current subscription or scheduled plan
     */
    getCurrentInterval(): SubscriptionInterval {
        let interval: SubscriptionInterval = this.interval;

        if (this.shouldUseScheduledPlan()) {
            interval = this.scheduled_plan.interval;
        }

        return interval;
    }

    /**
     * When the current subscription plan has been upgraded or downgraded, the scheduled plan is what we need to use
     * for showing the current plan
     */
    shouldUseScheduledPlan(): boolean {
        return this.scheduled_plan && !this.isCancelled;
    }

    isCustomPlan(): boolean {
        return isCustomPlan(this.metadata.plan_id) && !this.isCancelled;
    }

    get isLocationBilling(): boolean {
        return (
            this.metadata &&
            this.metadata.model === SubscriptionModel.PER_LOCATION
        );
    }

    get isSubscribed(): boolean {
        return this.status === SubscriptionStatus.ACTIVE;
    }

    get isCancelled(): boolean {
        return this.status === SubscriptionStatus.CANCELLED;
    }

    get isCancelling(): boolean {
        return this.status === SubscriptionStatus.CANCELLING;
    }

    get isTrialCancelling(): boolean {
        const todayMoment: Moment = moment();
        const trialEndMoment: Moment = getLocalMomentFromUtc(this.trial_end);
        return (
            this.isCancelling &&
            this.trial_end &&
            todayMoment.isBefore(trialEndMoment)
        );
    }

    get isLessThanFourDaysSinceTrialExpired(): boolean {
        const trialEndMoment: Moment = getLocalMomentFromUtc(this.trial_end);
        const todayMoment: Moment = moment();
        return (
            !this.trial_end ||
            (this.trial_end &&
                todayMoment.diff(trialEndMoment, MOMENT_UNIT.DAYS) < 4)
        );
    }

    get isLessThanMonthSinceTrialExpired() {
        const trialEndMoment: Moment = getLocalMomentFromUtc(this.trial_end);
        const todayMoment: Moment = moment();
        return (
            !this.trial_end ||
            (this.trial_end &&
                todayMoment.diff(trialEndMoment, MOMENT_UNIT.DAYS) < 30)
        );
    }

    get isSubscribedCancelling(): boolean {
        const cancellationEndMoment: Moment = getLocalMomentFromUtc(
            this.cancellation_end
        );
        const todayMoment: Moment = moment();
        return (
            this.isCancelling &&
            (!cancellationEndMoment.isValid() ||
                (cancellationEndMoment.isValid() &&
                    todayMoment.isBefore(cancellationEndMoment)))
        );
    }

    get isEnterprisePlan(): boolean {
        return (
            this.metadata && this.metadata.tier === SubscriptionTier.ENTERPRISE
        );
    }

    get isTrial(): boolean {
        return this.status === SubscriptionStatus.TRIAL;
    }

    get isTrialGrace(): boolean {
        return this.status === SubscriptionStatus.TRIAL_GRACE;
    }

    get trialDaysLeft(): number {
        let daysLeft: number = -1;
        if (this.isTrial || this.isTrialCancelling) {
            const todayMoment: Moment = moment().startOf('day');
            const trialEndMoment: Moment = getLocalMomentFromUtc(
                this.trial_end
            );

            daysLeft = trialEndMoment.diff(todayMoment, MOMENT_UNIT.DAYS);

            if (daysLeft < 0) {
                daysLeft = 0;
            }
        }
        return daysLeft; // indicates invalid, since the current state is not trial
    }

    get isTrialLast24(): boolean {
        let now: Moment = moment.utc();
        let trialEndMoment: Moment = moment.utc(
            this.trial_end,
            'YYYY-MM-DD HH:mm:ss'
        );
        let secondsLeftOnTrial: number = trialEndMoment.diff(
            now,
            MOMENT_UNIT.SECONDS
        );
        return (
            secondsLeftOnTrial > 0 && secondsLeftOnTrial < this.SECONDS_PER_DAY
        );
    }

    get isTrialExpiringInAWeek(): boolean {
        const trialDaysLeft: number = this.trialDaysLeft;
        return trialDaysLeft <= 7 && trialDaysLeft !== -1;
    }

    get isTrialExpiringInLessThanAWeek(): boolean {
        const trialDaysLeft: number = this.trialDaysLeft;
        return trialDaysLeft < 7 && trialDaysLeft !== -1;
    }

    get cancellationEndFormatted(): string {
        if (this.cancellation_end) {
            return getLocalMomentFromUtc(this.cancellation_end).format(
                'D MMM YYYY'
            );
        }
    }

    get periodEndFormatted(): string {
        if (this.period_end) {
            return getLocalMomentFromUtc(this.period_end).format('D MMM YYYY');
        }
    }
}
